
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Header from '@/components/Header';
import { ArrowRight, RefreshCw, Users, User } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import MultiDestination from '@/components/MultiDestination';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Connection, PublicKey, SystemProgram, clusterApiUrl } from '@solana/web3.js';
import { IDL } from '../utils/Tornado';
import { Program, AnchorProvider, BN } from '@coral-xyz/anchor'
import { useWallet } from '@solana/wallet-adapter-react';
import axios from 'axios';
import { program_address, vault_pda, server_url, chain } from '../utils/Constant';
import { Buffer } from 'buffer';

window.Buffer = Buffer;

const Index = () => {

  const wallet = useWallet();
  const connection = new Connection(clusterApiUrl(chain));

  const [amount, setAmount] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'single' | 'multi'>('single');
  const [multiDestinations, setMultiDestinations] = useState<Array<{ address: string; amount: string }>>([
    { address: '', amount: '' }
  ]);

  const navigate = useNavigate();
  const { toast } = useToast();

  const handleAddDestination = () => {
    if (multiDestinations.length < 10) {
      setMultiDestinations([...multiDestinations, { address: '', amount: '' }]);
    }
  };

  const handleRemoveDestination = (index: number) => {
    if (multiDestinations.length > 1) {
      const updated = [...multiDestinations];
      updated.splice(index, 1);
      setMultiDestinations(updated);
    }
  };

  const handleDestinationChange = (index: number, field: 'address' | 'amount', value: string) => {
    const updated = [...multiDestinations];
    updated[index][field] = value;
    setMultiDestinations(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if wallet is connected first
    if (!wallet.connected || !wallet.publicKey) {
      toast({
        title: "Wallet not connected",
        description: "Please connect your wallet to use WhirlPool",
        variant: "destructive",
      });
      return;
    }

    if (activeTab === 'single') {
      // Single destination validation
      if (!amount || parseFloat(amount) <= 0) {
        toast({
          title: "Invalid amount",
          description: "Please enter a valid SOL amount",
          variant: "destructive",
        });
        return;
      }

      if (!destinationAddress || destinationAddress.length < 32) {
        toast({
          title: "Invalid address",
          description: "Please enter a valid Solana address",
          variant: "destructive",
        });
        return;
      }

      setIsLoading(true);

      // const VAULT_PDA = PublicKey.findProgramAddressSync(
      //   [Buffer.from(VAULT_SEED)],
      //   PROGRAM_ID
      // )[0];

      // console.log(`vault_pda::`, VAULT_PDA.toBase58());

      // const program = new Program(IDL, PROGRAM_ID, provider);

      // const txSig = await program.methods
      //   .deposit(new BN(Number(amount) * 1e9))
      //   .accounts({
      //     depositor: wallet.publicKey,
      //     vault: VAULT_PDA,
      //     systemProgram: SystemProgram.programId
      //   })
      //   .rpc();

      // let res = await axios.post(`${server_url}/withdraw`, {
      //   amount: amount, destination: destinationAddress
      // });

      // if (res.data.result) {

      // } else {
      //   setIsLoading(false);
      // }

      navigate('/processing', {
        state: {
          amount,
          destinationAddress,
          isMulti: false
        }
      });

    } else {
      // Multi destination validation
      if (!amount || parseFloat(amount) <= 0) {
        toast({
          title: "Invalid amount",
          description: "Please enter a valid SOL amount",
          variant: "destructive",
        });
        return;
      }

      // Validate all destinations have valid addresses and amounts
      let isValid = true;
      let totalAllocated = 0;

      for (const [index, dest] of multiDestinations.entries()) {
        totalAllocated += parseFloat(dest.amount) || 0;

        if (!dest.address || dest.address.length < 32) {
          toast({
            title: "Invalid address",
            description: `Please enter a valid Solana address for destination ${index + 1}`,
            variant: "destructive",
          });
          isValid = false;
          break;
        }

        if (!dest.amount || parseFloat(dest.amount) <= 0) {
          toast({
            title: "Invalid amount",
            description: `Please enter a valid SOL amount for destination ${index + 1}`,
            variant: "destructive",
          });
          isValid = false;
          break;
        }
      }

      // Check if all SOL is allocated
      if (isValid) {
        const totalAmount = parseFloat(amount);
        const diff = Math.abs(totalAmount - totalAllocated);

        if (diff > 0.001) {
          toast({
            title: "Allocation mismatch",
            description: `Please allocate the full ${amount} SOL across all destinations`,
            variant: "destructive",
          });
          return;
        }

        setIsLoading(true);

        // const VAULT_PDA = PublicKey.findProgramAddressSync(
        //   [Buffer.from(VAULT_SEED)],
        //   PROGRAM_ID
        // )[0];

        // console.log(`vault_pda::`, VAULT_PDA.toBase58());

        // const program = new Program(IDL, PROGRAM_ID, provider);

        // const txSig = await program.methods
        //   .deposit(new BN(Number(amount) * 1e9))
        //   .accounts({
        //     depositor: wallet.publicKey,
        //     vault: VAULT_PDA,
        //     systemProgram: SystemProgram.programId
        //   })
        //   .rpc();

        // let res = await axios.post(`${server_url}/withdraw-multi`, {
        //   multiDestinations: multiDestinations
        // });

        // if (res.data.result) {

        // } else {
        //   setIsLoading(false);
        // }

        navigate('/processing', {
          state: {
            amount,
            multiDestinations,
            isMulti: true
          }
        });
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md glass-panel p-8">
          <div className="mb-6 text-center">
            <div className="flex justify-center items-center mb-4">
              <div className="flex justify-center items-center w-12 h-12">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="url(#paint0_linear)" strokeWidth="2" />
                  <path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke="url(#paint1_linear)" strokeWidth="2" />
                  <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" fill="url(#paint2_linear)" />
                  <defs>
                    <linearGradient id="paint0_linear" x1="2" y1="12" x2="22" y2="12" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#22D3EE" />
                      <stop offset="1" stopColor="#0EA5E9" />
                    </linearGradient>
                    <linearGradient id="paint1_linear" x1="6" y1="12" x2="18" y2="12" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#22D3EE" />
                      <stop offset="1" stopColor="#0EA5E9" />
                    </linearGradient>
                    <linearGradient id="paint2_linear" x1="10" y1="12" x2="14" y2="12" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#22D3EE" />
                      <stop offset="1" stopColor="#0EA5E9" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue bg-clip-text text-transparent">
              WhirlPool
            </h1>
            <p className="text-muted-foreground mt-2 flex items-center justify-center">
              <span>Anonymize your Solana transactions</span>
              <span className="inline-flex ml-2 justify-center items-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeOpacity="0.5" />
                  <path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke="currentColor" strokeWidth="2" strokeOpacity="0.5" />
                  <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" fill="currentColor" fillOpacity="0.5" />
                </svg>
              </span>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="amount">Total SOL Amount</Label>
              <div className="relative">
                <Input
                  id="amount"
                  type="number"
                  min="0.1"
                  step="0.01"
                  placeholder="0.0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="bg-secondary border-white/10 pr-12"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  SOL
                </div>
              </div>
            </div>

            <div className="border-t border-white/5 pt-5">
              <Tabs
                value={activeTab}
                onValueChange={(v) => setActiveTab(v as 'single' | 'multi')}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-2 mb-5">
                  <TabsTrigger value="single" className="flex items-center gap-1">
                    <User className="h-3.5 w-3.5" />
                    <span>Single</span>
                  </TabsTrigger>
                  <TabsTrigger value="multi" className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    <span>Multi</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="single" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="destination">Destination Address</Label>
                    <Input
                      id="destination"
                      placeholder="Enter Solana address"
                      value={destinationAddress}
                      onChange={(e) => setDestinationAddress(e.target.value)}
                      className="bg-secondary border-white/10"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="multi">
                  <div className="space-y-4">
                    <MultiDestination
                      destinations={multiDestinations}
                      onAdd={handleAddDestination}
                      onRemove={handleRemoveDestination}
                      onChange={handleDestinationChange}
                      totalAmount={amount}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-whirlpool-cyan to-whirlpool-blue whirlpool-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="mr-2 h-4 w-4" />
              )}
              {isLoading ? "Processing..." : "Continue"}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-white/5 text-center">
            <p className="text-xs text-muted-foreground">
              Fee: 0.5% + 0.005 SOL network fee
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Index;
